import React, { useEffect, useState } from 'react';
import '../../../styles/Order.scss';
import { tryFetch } from '../../../Helpers/FetchAPI';
import { getTokenOrDefault } from '../../../Helpers/Storage';
import { MdNavigateNext } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import PageLoading from '../../../Pages/layout/PageLoading';
import { useSnackbar } from 'notistack';

interface Media {
  media_url: string;
}

interface Item {
  product_name: string;
  quantity: number;
  media: Media[];
}

interface Order {
  id: string;
  order_reference: string;
  status: string;
  store_name: string;
  store_logo: string;
  items: Item[];
  order_total: string;
  shipping_delivery_code: string;
  shipping_calculation_id: string;
  has_review: boolean;
  merchant_note: string | null;
}

const Orders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('All');
  const [isPlacingOrder, setIsPlacingOrder] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const currency = process.env.REACT_APP_CURRENCY;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = getTokenOrDefault();
        const response = await tryFetch({
          url: '/g/orders',
          method: 'GET',
          token,
          query: '?page=1&size=10&limit=10',
        });

        if (response.is_success && Array.isArray(response.data)) {
          const transformedOrders: Order[] = response.data.map((order: any) => ({
            id: order.id,
            order_reference: order.order_reference,
            shipping_delivery_code: order.shipping_delivery_code,
            status: order.status,
            shipping_calculation_id:order.shipping_calculation_id,
            store_name: order.store_name,
            store_logo: order.store_logo,
            has_review: order.has_review === '1',
            merchant_note: order.merchant_note,
            items: Array.isArray(order.items)
              ? order.items.map((item: any) => ({
                  product_name: item.product_name,
                  quantity: item.quantity,
                  media: Array.isArray(item.media)
                    ? item.media.map((media: any) => ({
                        media_url: media.media_url,
                      }))
                    : [],
                }))
              : [], 
            order_total: order.order_total,
          }));
          setOrders(transformedOrders);
        } else {
          enqueueSnackbar(response.error || 'Failed to fetch orders.', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        enqueueSnackbar('Something went wrong while fetching your orders.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
  
    fetchOrders();
  }, []);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TO_PAY':
        return '#FF6500';
      case 'TO_SHIP':
        return '#A91079';
      case 'TO_RECEIVE':
        return '#4C3BCF';
      case 'COMPLETED':
        return '#832b99';
      case 'CANCELLED':
        return '#ff3333';
      case 'RETURN_REFUND':
        return '#3d3d3d';
      default:
        return '#000';
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      const token = getTokenOrDefault();
      const response = await tryFetch({
        url: `/g/orders/${orderId}/cancel`,
        method: 'POST',
        token,
      });

      if (response.is_success) {
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === orderId ? { ...order, status: 'CANCELLED' } : order
          )
        );
        enqueueSnackbar('Order cancelled successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(response.error || 'Failed to cancel order', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      enqueueSnackbar('Something went wrong. Please try again.', { variant: 'error' });
    }
  };

   const handlePlaceOrder = async (shipping_calculation_id: string, merchantNote: string | null) => {
    setIsPlacingOrder(true);
    try {
      const token = getTokenOrDefault();
      const payload = {
        request_id: shipping_calculation_id,
        merchant_note: merchantNote || '',
      };

      const response = await tryFetch({
        url: '/g/process-order',
        method: 'POST',
        token,
        payload,
      });

      if (response.is_success && response.data) {
         const orderData = response.data as { payment_url?: string };

      if (orderData.payment_url) {
        window.location.href = orderData.payment_url;
      } else {
        console.error('payment_url is missing in response:', orderData);
        enqueueSnackbar('Payment link not available. Please try again.', { variant: 'error' });
      }
    } else {
      console.error('Failed to process order:', response.errors);
      enqueueSnackbar('Failed to process payment. Please try again.', { variant: 'error' });
    }
  } catch (error) {
    console.error('Error processing order:', error);
    enqueueSnackbar('Error while processing payment.', { variant: 'error' });
  } finally {
    setIsPlacingOrder(false);
  }
};


  const filteredOrders = orders.filter((order) => 
    activeTab === 'All' || order.status === activeTab
  );

  if (loading) {
    return < PageLoading/>;
  }

  return (
    <div className="order-page">
      <h2 className="order-header">Your Orders</h2>
      <hr className="order-divider" />
      <div className="tabs">
        {['All', 'TO_PAY', 'TO_SHIP', 'TO_RECEIVE', 'COMPLETED', 'CANCELLED', 'RETURN_REFUND'].map((tab) => (
          <button
            key={tab}
            className={`tab-button ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      <div className="orders-container">
          {filteredOrders.length > 0 ? (
          filteredOrders.map((order, index) => (
            <div key={index} className="order-card">
              <div className="order-header-container">
                <p className="order-id">Reference No.: {order.order_reference}</p>
                <p className="secure-delivery-code">
                  Secure Delivery Code:
                </p>
              </div>
              <div className="order-status-container">
              <p className="order-status"
              style={{ color: getStatusColor(order.status) }}>
                Status: {order.status.replace(/_/g, ' ')}
              </p>
              <p className="secure-delivery-code"><span>{order.shipping_delivery_code}</span>  </p>
                </div>
              <hr className="divider" />
              <div className="merchant-details">
                <img
                  src={order.store_logo}
                  alt="Store Logo"
                  className="store-logo"
                />
                <p className="store-name">{order.store_name}</p>
                {order.status === 'TO_PAY' && (
                  <div className="action-buttons">

                    <button
                      className="pay-now-btn"
                      disabled={isPlacingOrder}
                      onClick={() => handlePlaceOrder(order.shipping_calculation_id, order.merchant_note)}
                    >
                      {isPlacingOrder ? 'Processing...' : 'Pay Now'}
                    </button>
                    
                  <button
                    className="cancel-order-btn"
                    onClick={() => handleCancelOrder(order.id)}
                  >
                    Cancel Order
                  </button>
                  
                  </div>
                )}
                 {order.status === 'COMPLETED' && !order.has_review && (
                  <button
                    className="review-btn"
                    onClick={() => navigate(`/p/review/${order.id}`)}
                  >
                    Add Review
                  </button>
                )}
              </div>
              <div className="product-list">
                {order.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="product-item">
                    <img
                      src={item.media[0]?.media_url || 'https://gottit-media.b-cdn.net/s/default-store-product-image.jpg'}
                      alt={item.product_name}
                      className="product-image"
                    />
                    <p className="product-name">{item.product_name}</p>
                  </div>
                ))}
              </div>
              <p className="item-count">
                Items: {order.items.length}
              </p>
              <p className="total-amount">
                Total Amount: {currency} {order.order_total}
              </p>
              <Link to={`/p/orders/${order.id}/${order.order_reference}`} className="next-icon">
                <MdNavigateNext size="28" />
              </Link>
            </div>
          ))
        ) : (
          <p className='no-order'>No orders found.</p>
        )}
      </div>
    </div>
  );
};

export default Orders;
